import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  type Edge,
  type FitViewOptions,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { BdGraphRowFloating, BdGraphRowPanel } from './BdGraphRow.js';
import {
  BD_GRAPH_NODE_WIDTH,
  BD_GRAPH_TONE_CLASS,
  bdGraphDecisions,
  bdGraphLayers,
  layoutBdGraph,
  type BdGraphTone,
} from './bd-graph-layout.js';
import { BD_FLOW_THEME_CLASS, BD_GRAPH_EDGE_TYPES, edgeRouting, HANDLE } from './bd-graph-edges.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdGraph as BdGraphData,
  BdGraphNode,
  BdSavedArtifact,
} from '../../../lib/api/basic-design.js';
import type { BdGraphDirection } from '../../../lib/bd-view-settings.js';

import '@xyflow/react/dist/style.css';

export interface BdGraphProps {
  /** 図の中身（API の `GET /{bdKey}/graph` の 1 枚） */
  graph: BdGraphData;
  /** 図の名前（読み上げと見出し） */
  label: string;
  /** 全節の定義（選んだノードの行を読むため） */
  defs: readonly BdArtifactDef[];
  /** 保存済みの節（選んだノードの行を読むため） */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 隠す列（個人の見え方） */
  hiddenLayers: readonly number[];
  /** 図の向き（個人の見え方） */
  direction: BdGraphDirection;
  /** 列の表示を切り替える */
  onToggleLayer: (layer: number) => void;
  /** 図の向きを変える */
  onDirectionChange: (direction: BdGraphDirection) => void;
  /** 節を開く。`sectionHref` があればそちらを使う */
  onOpenSection?: (artifact: BdArtifact) => void;
  /** 節を開くリンクの先。渡すと選んだ札の行に、ボタンではなくリンクを出す */
  sectionHref?: (artifact: BdArtifact) => string;
  /**
   * 図だけのページ向けに描く。枠を親の高さいっぱいに広げ、操作と選んだ札の行を図の上に浮かせる。
   * 既定は決まった高さの図と横の欄
   */
  fill?: boolean;
  /** 操作の並びの先頭に置くもの（図の種類の切り替えなど）。`fill` では図の上に一緒に浮かせる */
  toolbar?: ReactNode;
}

/** 札のノードが持つもの。 @graph-connects none */
interface BdNodeData extends Record<string, unknown> {
  node: BdGraphNode;
  tone: BdGraphTone;
  dimmed: boolean;
  selected: boolean;
  direction: BdGraphDirection;
}

/** 札のノード。 @graph-connects none */
type BdFlowNode = Node<BdNodeData, 'bd'>;

/**
 * ミニマップの札の色。React Flow の CSS が後から読まれて `fill-*` の class を上書きするため、
 * class ではなく `nodeColor`（札の style に載る）で渡す。
 * @graph-connects none
 */
const MINIMAP_COLOR: Readonly<Record<BdGraphTone, string>> = {
  new: 'var(--color-success)',
  modified: 'var(--color-warning)',
  removed: 'var(--color-error)',
  unchanged: 'var(--color-border-hover)',
  reused: 'var(--color-info)',
  decision: 'var(--color-accent-primary)',
  domain: 'var(--color-info)',
  degradation: 'var(--color-error)',
  dependency: 'var(--color-warning)',
  improvement: 'var(--color-success)',
  neutral: 'var(--color-border)',
};

/** ミニマップに渡す札の高さの目安（px）。2〜3 行の札の間をとる。 @graph-connects none */
const NODE_HEIGHT_HINT = 60;

/** 図の上に浮かせる操作の枠。下の図が少し透けるようにする。 @graph-connects none */
const FLOATING_PANEL_CLASS =
  'flex max-w-[calc(100%-2rem)] flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[color-mix(in_srgb,var(--color-bg-surface)_88%,transparent)] p-2 shadow-sm backdrop-blur-sm';

/**
 * 図だけのページで全体表示するときの余白。上は浮かせた操作の枠（2 段で約 120px）の下から
 * 札が始まるようにとる。
 * @graph-connects none
 */
const FILL_FIT_VIEW: FitViewOptions = {
  padding: { top: '150px', right: '40px', bottom: '40px', left: '40px' },
};

/** 判断・ドメインの線（つながりの説明であって流れではない）は破線にする。 @graph-connects none */
const DASHED_EDGE_KINDS = new Set(['decision', 'domain_entity']);

/**
 * 札 1 枚。線の出入り口は図の向きに合わせる（横なら左右、縦なら上下）。
 * @graph-connects none
 */
function BdGraphNodeView({ data }: NodeProps<BdFlowNode>) {
  const { t } = useTranslation();
  const lr = data.direction === 'LR';
  return (
    <div
      className={[
        'w-52 rounded-lg border px-3 py-2 text-xs shadow-sm transition-opacity',
        BD_GRAPH_TONE_CLASS[data.tone],
        data.dimmed ? 'opacity-25' : 'opacity-100',
        data.selected ? 'ring-2 ring-[var(--color-accent-primary)]' : '',
      ].join(' ')}
    >
      <Handle
        id={HANDLE.in}
        type="target"
        position={lr ? Position.Left : Position.Top}
        isConnectable={false}
      />
      <Handle
        id={HANDLE.revOut}
        type="source"
        position={lr ? Position.Left : Position.Top}
        isConnectable={false}
        className="opacity-0"
      />
      <Handle
        id={HANDLE.aroundIn}
        type="target"
        position={lr ? Position.Bottom : Position.Right}
        isConnectable={false}
        className="opacity-0"
      />
      <Handle
        id={HANDLE.sideIn}
        type="target"
        position={lr ? Position.Left : Position.Top}
        isConnectable={false}
        className="opacity-0"
      />
      <p className="text-[10px] uppercase tracking-wide opacity-70">
        {t(`bd.graph.kinds.${data.node.kind}`)}
      </p>
      <p className="truncate font-semibold" title={data.node.label}>
        {data.node.label}
      </p>
      {data.node.sub === undefined || data.node.sub === '' ? null : (
        <p className="truncate opacity-80" title={data.node.sub}>
          {data.node.sub}
        </p>
      )}
      <Handle
        id={HANDLE.out}
        type="source"
        position={lr ? Position.Right : Position.Bottom}
        isConnectable={false}
      />
      <Handle
        id={HANDLE.revIn}
        type="target"
        position={lr ? Position.Right : Position.Bottom}
        isConnectable={false}
        className="opacity-0"
      />
      <Handle
        id={HANDLE.aroundOut}
        type="source"
        position={lr ? Position.Bottom : Position.Right}
        isConnectable={false}
        className="opacity-0"
      />
      <Handle
        id={HANDLE.sideOut}
        type="source"
        position={lr ? Position.Left : Position.Top}
        isConnectable={false}
        className="opacity-0"
      />
    </div>
  );
}

/** React Flow に渡す札の種類。描くたびに作ると React Flow が警告を出すので外に置く。 @graph-connects none */
const NODE_TYPES: NodeTypes = { bd: BdGraphNodeView };

/**
 * 列の名前。列に入っているノードの種類から作る（全体図は「画面」「API・イベント」のように、
 * ER 図・画面遷移図は深さの番号と種類）。
 * @graph-connects none
 */
function useLayerLabels(graph: BdGraphData): Map<number, string> {
  const { t } = useTranslation();
  return useMemo(() => {
    const labels = new Map<number, string>();
    for (const layer of bdGraphLayers(graph)) {
      const kinds = [
        ...new Set(graph.nodes.filter((n) => n.layer === layer).map((n) => n.kind)),
      ].map((kind) => t(`bd.graph.kinds.${kind}`));
      labels.set(
        layer,
        t('bd.graph.layer', {
          n: layer + 1,
          kinds: kinds.join(t('bd.nextStep.pendingSeparator')),
        }),
      );
    }
    return labels;
  }, [graph, t]);
}

/**
 * 基本設計の動く図。拡大・縮小・移動・ミニマップ・全体表示ができ、列を
 * `layer` ごとに並べ、変更の種類と影響の向きで色を付ける。札を押すとその札とつながる札を
 * 強調し（ほかは薄くする）、横にその行の中身と節を開くボタンを出す。
 *
 * 列の表示と図の向きは見ている人ごとの見え方として呼び出し側が持つ。判断の絞り込みと選んだ札は
 * その場かぎり（開き直せば消える）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図・画面遷移図を、拡大・移動・ミニマップ・列の表示切り替え・判断の絞り込み・選んだ札の行の表示つきで描く
 * @graph-connects layoutBdGraph [calls] 札の配置と強調
 * @graph-connects BdGraphRowPanel [calls] 選んだ札の行（図の横の欄）
 * @graph-connects BdGraphRowFloating [calls] 選んだ札の行（図だけのページで図の上に浮かせる）
 */
export function BdGraph({
  graph,
  label,
  defs,
  saved,
  hiddenLayers,
  direction,
  onToggleLayer,
  onDirectionChange,
  onOpenSection,
  sectionHref,
  fill = false,
  toolbar = null,
}: BdGraphProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const [decision, setDecision] = useState<string>('');
  const layerLabels = useLayerLabels(graph);
  const decisions = bdGraphDecisions(graph);

  const layout = layoutBdGraph(graph, {
    hiddenLayers,
    direction,
    decision: decision === '' ? null : decision,
    selected,
  });
  const nodes: BdFlowNode[] = layout.nodes.map((placed) => ({
    id: placed.node.id,
    type: 'bd',
    position: { x: placed.x, y: placed.y },
    data: {
      node: placed.node,
      tone: placed.tone,
      dimmed: placed.dimmed,
      selected: placed.selected,
      direction,
    },
    // 札の寸法を書き戻す onNodesChange を持たないので、React Flow が測った寸法は渡した札に
    // 載らない。ミニマップは渡した札の寸法が無いと札を描かないため、目安の寸法を渡す
    initialWidth: BD_GRAPH_NODE_WIDTH,
    initialHeight: NODE_HEIGHT_HINT,
    draggable: false,
    connectable: false,
    ariaLabel: `${t(`bd.graph.kinds.${placed.node.kind}`)} ${placed.node.label}`,
  }));
  const edges: Edge[] = layout.edges.map((placed) => ({
    id: placed.edge.id,
    source: placed.edge.source,
    target: placed.edge.target,
    ...(placed.edge.label === undefined ? {} : { label: placed.edge.label }),
    labelBgPadding: [6, 3] as [number, number],
    labelBgBorderRadius: 4,
    ...edgeRouting(placed.route, placed.lane, direction === 'LR'),
    className: [
      DASHED_EDGE_KINDS.has(placed.edge.kind)
        ? '[&_.react-flow__edge-path]:[stroke-dasharray:4_3]'
        : '',
      placed.dimmed ? 'opacity-20' : '',
      placed.highlighted ? '[&_.react-flow__edge-path]:stroke-[var(--color-accent-primary)]' : '',
    ].join(' '),
    animated: placed.highlighted,
  }));
  const selectedNode = layout.nodes.find((n) => n.selected)?.node ?? null;
  const layers = bdGraphLayers(graph);

  const controls = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      <fieldset className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <legend className="sr-only">{t('bd.graph.layersAria', { title: label })}</legend>
        {layers.map((layer) => (
          <label key={layer} className="flex items-center gap-1 text-[var(--color-text-secondary)]">
            <Checkbox
              checked={!hiddenLayers.includes(layer)}
              onChange={() => {
                onToggleLayer(layer);
              }}
            />
            {layerLabels.get(layer)}
          </label>
        ))}
      </fieldset>
      <TabGroup ariaLabel={t('bd.graph.directionAria')}>
        {(['LR', 'TB'] as const).map((d) => (
          <TabButton
            key={d}
            active={direction === d}
            label={t(`bd.graph.direction.${d}`)}
            onClick={() => {
              onDirectionChange(d);
            }}
          />
        ))}
      </TabGroup>
      {decisions.length === 0 ? null : (
        <div className="w-56">
          <Select
            size="sm"
            fullWidth
            aria-label={t('bd.graph.decisionFilter')}
            options={[
              { value: '', label: t('bd.graph.allDecisions') },
              ...decisions.map((d) => ({ value: d.id, label: d.label })),
            ]}
            value={decision}
            onChange={(e) => {
              setDecision(e.target.value);
              setSelected(null);
            }}
          />
        </div>
      )}
    </div>
  );
  const rowProps = {
    node: selectedNode,
    defs,
    saved,
    onOpenSection,
    sectionHref,
    onClose: () => {
      setSelected(null);
    },
  };
  const flow = (overlay: ReactNode, fitViewOptions?: FitViewOptions) => (
    <ReactFlow
      // 向き・列・絞り込みが変わったら作り直して、全体表示をし直す
      key={`${direction}:${hiddenLayers.join(',')}:${decision}`}
      nodes={nodes}
      edges={edges}
      nodeTypes={NODE_TYPES}
      edgeTypes={BD_GRAPH_EDGE_TYPES}
      nodesDraggable={false}
      nodesConnectable={false}
      className={BD_FLOW_THEME_CLASS}
      fitView
      {...(fitViewOptions === undefined ? {} : { fitViewOptions })}
      minZoom={0.2}
      onNodeClick={(_event, node) => {
        setSelected((current) => (current === node.id ? null : node.id));
      }}
      onPaneClick={() => {
        setSelected(null);
      }}
    >
      {overlay}
      <Background />
      <Controls showInteractive={false} />
      <MiniMap<BdFlowNode>
        pannable
        zoomable
        ariaLabel={t('bd.graph.minimap')}
        nodeColor={(node) => MINIMAP_COLOR[node.data.tone]}
      />
    </ReactFlow>
  );
  const hiddenAll = (
    <p className="p-4 text-sm text-[var(--color-text-muted)]">{t('bd.graph.allHidden')}</p>
  );

  if (fill) {
    // 図だけのページ: 枠を画面いっぱいに使い、操作と選んだ札の行は図の上に浮かせる（行は図を
    // 覆わないので、開いたまま別の札を押せる）
    return (
      <div role="group" aria-label={label} className="relative h-full min-h-0">
        {nodes.length === 0 ? (
          <div className="space-y-3 p-3">
            {toolbar}
            {controls}
            {hiddenAll}
          </div>
        ) : (
          flow(
            <>
              <Panel position="top-left" className={FLOATING_PANEL_CLASS}>
                {toolbar}
                {controls}
              </Panel>
              {selectedNode === null ? null : (
                <Panel position="top-right">
                  <BdGraphRowFloating {...rowProps} />
                </Panel>
              )}
            </>,
            FILL_FIT_VIEW,
          )
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {toolbar}
      {controls}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_18rem]">
        <div
          role="group"
          aria-label={label}
          className="h-[28rem] overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)]"
        >
          {nodes.length === 0 ? hiddenAll : flow(null)}
        </div>
        <BdGraphRowPanel {...rowProps} />
      </div>
    </div>
  );
}
